import { useEffect, useState } from "react";
import { adm } from "../../lib/adminApi";
import { PageHead, Card, Sel } from "../../components/admin/ui";

const LABELS = {
  page_view: "Page views", hero_dimension_select: "Hero dimension selects", hero_play: "Hero track plays",
  track_play: "Track plays", enter_dimension: "Enter Dimension clicks", release_view: "Release views",
  track_view: "Track page views", store_view: "Store views", product_view: "Product views",
  complete_set_view: "Complete Set views", checkout_start: "Checkouts started",
  dv_redeemed: "Dimension V redemptions", supporters_view: "Supporters views",
  newsletter_join: "Newsletter joins", visual_open: "Visuals opened",
};

function Stat({ label, value }) {
  return (
    <div className="border border-white/10 bg-[#0c0c0c] p-5">
      <p className="font-mono text-[8px] tracking-[0.25em] text-white/40">{label.toUpperCase()}</p>
      <p className="font-serif text-3xl text-white mt-2">{value ?? 0}</p>
    </div>
  );
}

export default function AdminAnalytics() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);

  useEffect(() => { adm.analytics(days).then(setData).catch(() => {}); }, [days]);
  if (!data) return <p className="text-white/40 font-mono text-xs">LOADING…</p>;

  const maxDay = Math.max(1, ...(data.dailyPageViews || []).map((d) => d.views));

  return (
    <div data-testid="admin-analytics">
      <PageHead title="Analytics" sub="Listener engagement signals. No invasive tracking — anonymous, aggregated events only.">
        <Sel label="" value={days} onChange={setDays}
          options={[{ v: "7", l: "LAST 7 DAYS" }, { v: "30", l: "LAST 30 DAYS" }, { v: "90", l: "LAST 90 DAYS" }]}
          testid="analytics-days" />
      </PageHead>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Unique visitors" value={data.uniqueVisitors} />
        <Stat label="Page views" value={data.byType?.page_view} />
        <Stat label="Track plays" value={(data.byType?.track_play || 0) + (data.byType?.hero_play || 0)} />
        <Stat label="Enter dimension" value={data.byType?.enter_dimension} />
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <Card>
          <p className="font-mono text-[10px] tracking-[0.3em] text-gold mb-4">EVENTS</p>
          {Object.entries(data.byType || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
            <div key={k} className="flex justify-between py-2 border-t border-white/5 text-sm">
              <span className="text-white/60">{LABELS[k] || k}</span>
              <span className="text-white font-mono">{v}</span>
            </div>
          ))}
          {!Object.keys(data.byType || {}).length && <p className="text-white/30 text-sm">No events yet.</p>}
        </Card>
        <Card>
          <p className="font-mono text-[10px] tracking-[0.3em] text-gold mb-4">DAILY PAGE VIEWS</p>
          {(data.dailyPageViews || []).slice(-21).map((d) => (
            <div key={d.date} className="flex items-center gap-3 py-1">
              <span className="font-mono text-[9px] text-white/40 w-20">{d.date.slice(5)}</span>
              <div className="flex-1 h-2 bg-white/5"><div className="h-2 bg-gold/70" style={{ width: `${(d.views / maxDay) * 100}%` }} /></div>
              <span className="font-mono text-[9px] text-white/60 w-8 text-right">{d.views}</span>
            </div>
          ))}
          {!(data.dailyPageViews || []).length && <p className="text-white/30 text-sm">No page views yet.</p>}
        </Card>
        <Card>
          <p className="font-mono text-[10px] tracking-[0.3em] text-gold mb-4">TOP TRACKS</p>
          {(data.topTracks || []).map((t) => (
            <div key={t.title} className="flex justify-between py-2 border-t border-white/5 text-sm">
              <span className="text-white/70">{t.title}</span>
              <span className="text-white font-mono">{t.plays}</span>
            </div>
          ))}
          {!(data.topTracks || []).length && <p className="text-white/30 text-sm">No plays yet.</p>}
        </Card>
        <Card>
          <p className="font-mono text-[10px] tracking-[0.3em] text-gold mb-4">EXTERNAL REFERRERS</p>
          {(data.referrers || []).map((r) => (
            <div key={r.domain} className="flex justify-between py-2 border-t border-white/5 text-sm">
              <span className="text-white/70">{r.domain}</span>
              <span className="text-white font-mono">{r.visits}</span>
            </div>
          ))}
          {!(data.referrers || []).length && <p className="text-white/30 text-sm">No external referrals recorded yet.</p>}
        </Card>
      </div>
    </div>
  );
}
